"use client";
import classNames from "classnames";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Icon from "./icon";

type HorizontalScrollerProps = {
  children: ReactNode;
  className?: string;
  size?: "sm" | "md";
  /** Selector for the active item, kept in view on mount and when `activeKey` changes. */
  activeSelector?: string;
  activeKey?: unknown;
};

// Width covered by an arrow + fade; an active item under it counts as hidden.
const EDGE_PADDING = 48;
// Content is fully masked out under the arrow, then fades in. In rem to
// match the w-7/w-8 buttons at any root font size.
const ARROW_ZONE = { sm: "1.75rem", md: "2rem" };
const FADE_WIDTH = 16;

// Masking the content itself (rather than overlaying a gradient) lets whatever
// sits behind show through, so it works on any background and in dark mode.
const edgeMask = (
  left: boolean,
  right: boolean,
  zone: string,
  fade: number,
) => {
  const start = left
    ? `transparent ${zone}, black calc(${zone} + ${fade}px)`
    : "black 0";
  const end = right
    ? `black calc(100% - ${zone} - ${fade}px), transparent calc(100% - ${zone})`
    : "black 100%";
  return `linear-gradient(to right, ${start}, ${end})`;
};

/**
 * Horizontally scrolling row with arrow buttons, since mouse users without a
 * trackpad can't scroll sideways. On touch they also signal that there is
 * more to swipe to. The arrows are a pointer convenience only: keyboard users reach every item via Tab (which
 * scrolls it into view), so the buttons stay out of the tab order and the
 * accessibility tree.
 */
const HorizontalScroller = ({
  children,
  className,
  size = "md",
  activeSelector,
  activeKey,
}: HorizontalScrollerProps) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hasMounted = useRef(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollerRef.current;
    const active = activeSelector
      ? el?.querySelector<HTMLElement>(activeSelector)
      : null;
    const isFirstRun = !hasMounted.current;
    hasMounted.current = true;
    if (!el || !active) return;
    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    const isHidden =
      left < el.scrollLeft + EDGE_PADDING ||
      right > el.scrollLeft + el.clientWidth - EDGE_PADDING;
    if (!isHidden) return;
    el.scrollTo({
      left: left - el.clientWidth / 2 + active.offsetWidth / 2,
      behavior: isFirstRun ? "auto" : "smooth",
    });
  }, [activeSelector, activeKey]);

  const scrollBy = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  };

  // No fill of their own: the content is masked out under the arrows, so they
  // sit on whatever is behind. gray-500 is the text colour (black, light in
  // dark mode), well above 3:1 (WCAG 1.4.11) against sand-500/600 even on hover.
  const buttonClass = classNames(
    "pointer-events-auto flex h-full items-center justify-center",
    "text-gray-500 hover:opacity-70",
    size === "sm" ? "w-7" : "w-8",
  );
  const iconClass = size === "sm" ? "size-4" : "size-5";
  const edgeClass =
    "pointer-events-none absolute top-0 z-20 flex h-full items-center";
  const mask =
    canScrollLeft || canScrollRight
      ? edgeMask(canScrollLeft, canScrollRight, ARROW_ZONE[size], FADE_WIDTH)
      : undefined;

  return (
    <div className={classNames("relative", className)}>
      {canScrollLeft && (
        <div className={classNames(edgeClass, "left-0")}>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(-1)}
            className={buttonClass}
          >
            <Icon.ChevronDown
              strokeWidth={2.5}
              className={classNames(iconClass, "rotate-90")}
            />
          </button>
        </div>
      )}
      {canScrollRight && (
        <div className={classNames(edgeClass, "right-0")}>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(1)}
            className={buttonClass}
          >
            <Icon.ChevronDown
              strokeWidth={2.5}
              className={classNames(iconClass, "-rotate-90")}
            />
          </button>
        </div>
      )}
      <div
        ref={scrollerRef}
        onScroll={updateScrollState}
        className="relative z-10 overflow-x-auto scrollbar-none"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        {/* Sized to its content so the ResizeObserver fires when the items
            change, e.g. a new submenu after client-side navigation. A
            full-width child would keep its size and leave the arrows stale. */}
        <div ref={contentRef} className="w-max min-w-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default HorizontalScroller;
